import React from 'react';
import { motion } from 'framer-motion';
import { Home, BarChart2, Radio, Bell, User, TrendingUp, TrendingDown, Check, BadgeCheck, Copy } from 'lucide-react';

/* ---------- Realistic iPhone frame ---------- */
export const DeviceFrame = ({ children, className = '', width = 'w-[300px]', tilt = false, glow = true, radius = 'md' }) => {
  const R = radius === 'sm'
    ? { rail: 'rounded-[1.35rem]', bezel: 'rounded-[1.22rem]', screen: 'rounded-[0.9rem]' }
    : { rail: 'rounded-[2.05rem]', bezel: 'rounded-[1.9rem]', screen: 'rounded-[1.45rem]' };
  return (
  <div className={`relative ${width} ${className}`} style={tilt ? { transform: 'perspective(1600px) rotateY(-9deg) rotateX(3deg)' } : {}}>
    {glow && <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[4rem] bg-brand/25 blur-[80px]" />}

    {/* side buttons */}
    <div className="absolute -left-[2px] top-[19%] z-0 h-7 w-[3px] rounded-l-sm bg-gradient-to-b from-[#3a4a54] to-[#1a262e]" />
    <div className="absolute -left-[2px] top-[27%] z-0 h-10 w-[3px] rounded-l-sm bg-gradient-to-b from-[#3a4a54] to-[#1a262e]" />
    <div className="absolute -left-[2px] top-[38%] z-0 h-10 w-[3px] rounded-l-sm bg-gradient-to-b from-[#3a4a54] to-[#1a262e]" />
    <div className="absolute -right-[2px] top-[30%] z-0 h-14 w-[3px] rounded-r-sm bg-gradient-to-b from-[#3a4a54] to-[#1a262e]" />

    {/* titanium rail */}
    <div className={`relative ${R.rail} bg-gradient-to-br from-[#4a5a64] via-[#0d1c26] to-[#05121b] p-[3px] shadow-[0_60px_120px_-30px_rgba(0,0,0,0.9)]`}>
      {/* black bezel */}
      <div className={`relative ${R.bezel} bg-black p-[8px]`}>
        <div className={`relative aspect-[9/19.5] overflow-hidden ${R.screen} bg-black`}>
          {children}
          {/* dynamic island */}
          <div className="absolute left-1/2 top-[11px] z-30 flex h-[24px] w-[82px] -translate-x-1/2 items-center justify-end gap-1.5 rounded-full bg-[#050505] pr-2.5 ring-1 ring-white/[0.06]">
            <span className="h-[7px] w-[7px] rounded-full bg-[#0a1a22] ring-1 ring-white/10" />
          </div>
          {/* screen gloss */}
          <div className={`pointer-events-none absolute inset-0 z-40 ${R.screen} bg-[linear-gradient(120deg,rgba(255,255,255,0.10)_0%,transparent_22%,transparent_78%,rgba(255,255,255,0.05)_100%)]`} />
        </div>
      </div>
    </div>
  </div>
  );
};

const StatusBar = ({ dark = false }) => (
  <div className={`flex items-center justify-between px-6 pt-3.5 text-[10px] font-semibold ${dark ? 'text-white/80' : 'text-white/85'}`}>
    <span>9:41</span>
    <span className="flex items-center gap-1"><span className="tracking-tighter">●●●</span><span>5G</span><span className="ml-0.5 inline-block h-2.5 w-4 rounded-[3px] border border-white/50" /></span>
  </div>
);

const Avatar = ({ n, color = '#17FDFD' }) => (
  <span className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold" style={{ background: `${color}22`, color }}>{n}</span>
);

const Spark = ({ up = true, className = 'h-5 w-12' }) => (
  <svg viewBox="0 0 60 22" className={className} fill="none" preserveAspectRatio="none">
    <path d={up ? 'M0 18 L12 12 L24 15 L36 6 L48 9 L60 2' : 'M0 4 L12 9 L24 6 L36 14 L48 11 L60 19'}
      stroke={up ? '#00FFA3' : '#FF4D6D'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TabBar = ({ active = 2 }) => {
  const icons = [Home, BarChart2, Radio, Bell, User];
  return (
    <div className="absolute inset-x-0 bottom-0 flex items-center justify-around border-t border-white/10 bg-black/95 px-4 pb-4 pt-2.5 backdrop-blur">
      {icons.map((Icon, i) => (
        <div key={i} className="relative flex flex-col items-center">
          {i === active && <span className="absolute -top-2.5 h-1 w-1 rounded-full bg-brand" style={{ filter: 'drop-shadow(0 0 4px #17FDFD)' }} />}
          <Icon className={`h-4 w-4 ${i === active ? 'text-brand' : 'text-white/35'}`} />
        </div>
      ))}
    </div>
  );
};

const SignalCard = ({ provider, pair, side, price, entry, sl, tp, conf, color, delay }) => {
  const buy = side === 'BUY';
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay, duration: 0.5 }}
      className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Avatar n={provider[0]} color={color} />
          <span className="text-[10px] font-medium text-white/70">{provider}</span>
        </div>
        <span className="flex items-center gap-0.5 text-[8px] font-medium text-[#00FFA3]"><Check className="h-2.5 w-2.5" /> Copied</span>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="text-[13px] font-bold text-white">{pair}</span>
          <span className={`flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[9px] font-bold ${buy ? 'bg-[#00FFA3]/15 text-[#00FFA3]' : 'bg-[#FF4D6D]/15 text-[#FF4D6D]'}`}>
            {buy ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}{side}
          </span>
        </div>
        <Spark up={buy} />
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[8px] text-white/40">
        <span className="rounded bg-white/5 px-1.5 py-0.5">Bid {price}</span>
        <span>via {provider}</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-1 text-[9px]">
        {[['Entry', entry], ['SL', sl], ['TP', tp]].map(([k, v]) => (
          <div key={k} className="rounded-lg bg-black/30 px-2 py-1"><p className="text-white/40">{k}</p><p className="mt-0.5 font-semibold text-white">{v}</p></div>
        ))}
      </div>
    </motion.div>
  );
};

export const SignalsScreen = () => (
  <div className="flex h-full flex-col bg-[radial-gradient(120%_55%_at_50%_0%,#052330_0%,#000000_62%)]">
    <StatusBar />
    {/* MT5 account / risk */}
    <div className="mx-4 mt-2 rounded-2xl border border-brand/20 bg-gradient-to-br from-brand/15 to-transparent p-3">
      <div className="flex items-center justify-between">
        <p className="text-[9px] uppercase tracking-wider text-white/50">My Account · <span className="font-bold text-[#00FFA3]">LIVE</span></p>
        <span className="rounded-md bg-brand/15 px-1.5 py-0.5 text-[9px] font-bold text-brand">Risk 1.0x</span>
      </div>
      <div className="mt-0.5 flex items-end justify-between">
        <span className="font-display text-[22px] font-bold leading-none text-white">$50,010.95</span>
        <span className="text-[9px] text-white/40">MetaTrader 5</span>
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <span className="flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[8px] text-white/70"><Copy className="h-2.5 w-2.5 text-brand" /> Auto-copying 4 channels</span>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between px-4">
      <h4 className="font-display text-sm font-bold text-white">Live Signals</h4>
      <span className="flex items-center gap-1 rounded-full bg-brand/15 px-2 py-0.5 text-[9px] font-semibold text-brand"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" /> Live</span>
    </div>
    <div className="mt-2 flex gap-1.5 px-4">
      {['All', 'Forex', 'Metals', 'Crypto'].map((f, i) => (
        <span key={f} className={`rounded-full px-2.5 py-1 text-[9px] font-medium ${i === 0 ? 'bg-brand text-black' : 'bg-white/5 text-white/50'}`}>{f}</span>
      ))}
    </div>
    <div className="mt-2.5 flex-1 space-y-2 overflow-hidden px-4 pb-16">
      <SignalCard provider="Gold Channel" pair="XAU/USD" side="BUY" entry="2351.4" sl="2344.9" tp="2372.5" conf={92} color="#17FDFD" delay={0.2} />
      <SignalCard provider="FX Channel" pair="EUR/USD" side="SELL" entry="1.0845" sl="1.0880" tp="1.0790" conf={78} color="#00FFA3" delay={0.35} />
    </div>
    <TabBar active={2} />
  </div>
);

export const ProviderScreen = () => (
  <div className="flex h-full flex-col bg-[radial-gradient(120%_55%_at_50%_0%,#052330_0%,#000000_62%)]">
    <StatusBar />
    <div className="flex items-center gap-2.5 px-4 pt-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/15 font-display text-sm font-bold text-brand">P</span>
      <div>
        <p className="flex items-center gap-1 text-[11px] font-semibold text-white">Provider Studio <BadgeCheck className="h-3 w-3 text-brand" /></p>
        <p className="text-[9px] text-white/45">Verified provider</p>
      </div>
    </div>
    <div className="mx-4 mt-3 grid grid-cols-2 gap-2">
      {[['128', 'Total followers'], ['36', 'Signals (7d)'], ['3', 'Active trades'], ['412', 'Total signals']].map(([v, k]) => (
        <div key={k} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
          <p className="font-display text-lg font-bold text-white">{v}</p>
          <p className="text-[8px] uppercase tracking-wide text-white/40">{k}</p>
        </div>
      ))}
    </div>
    <div className="mx-4 mt-3 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
      <p className="text-[9px] text-white/50">Follower growth</p>
      <svg viewBox="0 0 220 56" className="mt-2 h-14 w-full" preserveAspectRatio="none">
        <defs><linearGradient id="ar" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#17FDFD" stopOpacity="0.5" /><stop offset="100%" stopColor="#17FDFD" stopOpacity="0" /></linearGradient></defs>
        <path d="M0 50 C30 46 40 34 70 34 C100 34 110 16 150 16 C180 16 195 6 220 5 L220 56 L0 56 Z" fill="url(#ar)" />
        <path d="M0 50 C30 46 40 34 70 34 C100 34 110 16 150 16 C180 16 195 6 220 5" stroke="#17FDFD" strokeWidth="2" fill="none" />
      </svg>
    </div>
    <div className="mx-4 mt-2.5">
      <p className="mb-1.5 text-[9px] uppercase tracking-wider text-white/40">Your channels</p>
      {[['Copy.click Channel', '5 followers'], ['Gold Setups', '3 followers']].map(([m, v]) => (
        <div key={m} className="mb-1.5 flex items-center justify-between rounded-lg bg-white/[0.04] px-2.5 py-1.5 text-[10px]">
          <span className="text-white/70">{m}</span><span className="text-white/45">{v}</span>
        </div>
      ))}
    </div>
    <TabBar active={1} />
  </div>
);

export const RiskScreen = () => (
  <div className="flex h-full flex-col bg-[radial-gradient(120%_55%_at_50%_0%,#052330_0%,#000000_62%)]">
    <StatusBar />
    <div className="px-4 pt-3"><h4 className="font-display text-base font-bold text-white">Risk Manager</h4></div>
    <div className="mt-4 flex flex-col items-center">
      <div className="relative flex h-28 w-28 items-center justify-center">
        <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
          <circle cx="50" cy="50" r="42" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" />
          <circle cx="50" cy="50" r="42" stroke="#17FDFD" strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray="264" strokeDashoffset="200" style={{ filter: 'drop-shadow(0 0 6px #17FDFD)' }} />
        </svg>
        <div className="absolute text-center"><p className="font-display text-xl font-bold text-white">1.0%</p><p className="text-[8px] text-white/40">per trade</p></div>
      </div>
    </div>
    <div className="mt-4 space-y-2 px-4">
      {['Max daily loss', 'Auto lot sizing', 'Equity protection'].map((t) => (
        <div key={t} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
          <span className="text-[11px] text-white/80">{t}</span>
          <span className="relative h-4 w-8 rounded-full bg-brand/40"><span className="absolute right-0.5 top-0.5 h-3 w-3 rounded-full bg-brand" style={{ filter: 'drop-shadow(0 0 4px #17FDFD)' }} /></span>
        </div>
      ))}
      <div className="pt-1.5">
        <div className="rounded-2xl bg-brand py-2.5 text-center text-[11px] font-bold text-black shadow-[0_0_24px_rgba(23,253,253,0.45)]">Save Settings</div>
      </div>
    </div>
    <TabBar active={2} />
  </div>
);

export const SCREENS = { signals: SignalsScreen, provider: ProviderScreen, risk: RiskScreen };
